import { Maybe } from 'aem-libs-ts/src/types/utility'

declare const WEBPACK_INLET_NAME: string

declare global {
  interface Window {
    dataLayer: unknown[]
  }
}

export const enum EventType {
  CALCULATOR_INPUT = 'calculator_input',
  CALCULATOR_INTERACTION = 'calculator_interaction',
  FORM_CHANGE = 'form_data_change',
  CLICK = 'click',
  VIEW = 'calculator_view',
  PAGE_VIEW = 'page_view',
  AB_TESTING = 'testing_ab',
  CTA = 'cta',
  FORM_SUBMIT = 'form_submit',
  CONFIGURATION_CHANGE = 'configuration_change',
  MODAL_CHANGE = 'modal',
  REDIRECT = 'redirect'
}

const INIT_EVENT = 'initial'

export type CtaElementTarget =
  | 'clickPrimaryGeorgeEntranceDropdown'
  | 'clickPrimaryNonGeorgeEntrance'
  | 'clickPrimaryGeorgeEntrance'
  | 'clickPrimaryCMBEntrance'
  | 'clickPrimaryBookingOfflineEntrance'
export type InteractionMethod = 'slider' | 'button' | 'editbox' | 'dropdown' | 'radio'
export type ElementLocation = 'Kalkulačka'
export type ClickType = 'cta' | 'internal_link' | 'external_link' | 'button' | 'modal_close'

export type InputChangeValue = string | number | boolean

const defaultValueKey = 'value'

interface CommonEvent {
  event: EventType
  feApplicationName: string
}

export interface ConfigurationChangePayload {
  interactionType: string
  elementText: string
  elementName: string
}

export interface InputEventPayload {
  elementName: string
  eventDescription?: string
  method: InteractionMethod
  elementID: string
}

export interface TestingPayload {
  testingABVariant: string
  testingABName: string
}

export interface InputChangeEventPayload<TValue> {
  elementID: string
  elementName: string
  method: InteractionMethod
  value?: TValue
  event?: EventType
}

export interface ClickEventPayload {
  clickType: ClickType
  elementText: string
  elementLocation: ElementLocation | string
  elementID: string
}

export interface CtaPayload {
  elementTarget: CtaElementTarget
  deepLink?: boolean
}

export interface ViewEventPayload {
  appName: string
}

export interface PageViewPayload {
  screenTitle: string
  documentPath: string
}

export interface ModalChangePayload {
  modalTitle: string
  status: 'shown' | 'closed'
}

export type FormSubmitPayload = Record<string, { elementID: string; elementName?: string; value: string[] }>

type PageViewEvent = CommonEvent & PageViewPayload
type InputEvent = CommonEvent & InputEventPayload
type InteractionEvent<TData extends object> = CommonEvent & TData & { eventDescription?: string }
type FormChangeEvent<TData extends object> = CommonEvent & TData
type ClickEvent = CommonEvent & ClickEventPayload
type ViewEvent = CommonEvent & ViewEventPayload
type ABTestingEvent = CommonEvent & TestingPayload
type FormSubmitEvent = CommonEvent & {
  formID: string
  formName: string
  data: FormSubmitPayload
}

interface RedirectEventPayload {
  url: string
  deepLink: boolean
  elementText: string
  elementID: string
}

interface GaTrackerType {
  withDefaults: (defaults: Record<string, unknown>) => GaTrackerType
  withVariant: (variant: Maybe<string>) => GaTrackerType
  onInputChange: <TValue>(eventPayload: InputChangeEventPayload<TValue>) => void
  onInput: <TData extends object>(eventPayload: InputEventPayload, data: TData) => void
  onFormChange: <TData extends object>(data: TData) => void
  onInteraction: <TData extends object>(data: TData, initInteraction?: boolean) => void
  onClick: (data: ClickEventPayload) => void
  onView: (appName: string) => void
  onTestingView: (payload: TestingPayload) => void
  onPageView: (payload: PageViewPayload) => void
  onCustomEvent: <TData extends object>(eventPayload: TData) => void
  onCtaClick: (payload: CtaPayload) => void
  onConfigurationChange: (payload: ConfigurationChangePayload) => void
  onFormSubmit: (formID: string, formName: string, payload: FormSubmitPayload, processId?: string) => void
  onModalChange: (payload: ModalChangePayload) => void
  onRedirect: (payload: RedirectEventPayload) => void
}

export const gaTracker = (() => {
  let defaultPayload: Record<string, unknown> = {}

  const createEvent = <T extends Omit<CommonEvent, 'feApplicationName'>>(
    eventData: T
  ): T & Pick<CommonEvent, 'feApplicationName'> => ({
    ...eventData,
    ...defaultPayload,
    feApplicationName: WEBPACK_INLET_NAME
  })

  const push = <T extends object>(obj: T) => {
    if (typeof window.dataLayer === 'undefined') {
      // eslint-disable-next-line no-console
      console.log('Missing dataLayer to push: ' + JSON.stringify(obj))
    } else {
      setTimeout(() => {
        const pushEvent = new CustomEvent('dl-push', { detail: obj })
        window.dispatchEvent(pushEvent)
        window.dataLayer.push(obj)
      }, 0)
    }
  }

  const tracker: GaTrackerType = {
    withDefaults: (defaults: Record<string, unknown>) => {
      defaultPayload = { ...defaultPayload, ...defaults }
      return tracker
    },
    withVariant: (variant: Maybe<string>) => {
      return tracker.withDefaults(variant ? { variant } : defaultPayload)
    },
    onInputChange: function <TValue>({ value, event, ...eventPayload }: InputChangeEventPayload<TValue>) {
      const valueKey = eventPayload?.elementID ?? defaultValueKey
      const evt: InputEvent = createEvent({
        ...eventPayload,
        [valueKey]: value,
        event: event ?? EventType.CALCULATOR_INPUT,
        eventDescription: `Změna ${eventPayload.elementID}`
      })
      push(evt)
    },

    onInput: function <TData extends object>(eventPayload: InputEventPayload, data: TData) {
      const evt: InputEvent = createEvent({
        event: EventType.CALCULATOR_INPUT,
        ...eventPayload,
        ...data,
        eventDescription: `Změna ${eventPayload.elementID}`
      })
      push(evt)
    },

    onFormChange: function <TData extends object>(data: TData) {
      const evt: FormChangeEvent<TData> = createEvent({ event: EventType.FORM_CHANGE, ...data })
      push(evt)
    },

    onInteraction: function <TData extends object>(data: TData, initInteraction = false) {
      const evt: InteractionEvent<TData> = createEvent({
        event: EventType.CALCULATOR_INTERACTION,
        ...(initInteraction && { eventDescription: INIT_EVENT }),
        ...data
      })
      push(evt)
    },

    onClick: (data: ClickEventPayload) => {
      const evt: ClickEvent = createEvent({ event: EventType.CLICK, ...data })
      push(evt)
    },

    onView: (appName: string) => {
      const evt: ViewEvent = createEvent({ event: EventType.VIEW, appName })
      push(evt)
    },
    onTestingView: (payload: TestingPayload) => {
      const evt: ABTestingEvent = createEvent({ ...payload, event: EventType.AB_TESTING })
      push(evt)
    },
    onPageView: (payload: PageViewPayload) => {
      const evt: PageViewEvent = createEvent({ ...payload, event: EventType.PAGE_VIEW })
      push(evt)
    },
    onCustomEvent: function <TData extends object>(eventPayload: TData) {
      push({
        ...eventPayload,
        ...defaultPayload,
        feApplicationName: WEBPACK_INLET_NAME
      })
    },
    onCtaClick: (payload: CtaPayload) => {
      const evt: InteractionEvent<CtaPayload> = createEvent({ event: EventType.CTA, ...payload })
      push(evt)
    },
    onConfigurationChange: (payload: ConfigurationChangePayload) => {
      push(createEvent({ event: EventType.CONFIGURATION_CHANGE, ...payload }))
    },
    onModalChange: (payload: ModalChangePayload) => {
      push(createEvent({ event: EventType.MODAL_CHANGE, ...payload }))
    },
    onRedirect: (payload: RedirectEventPayload) => {
      push(createEvent({ event: EventType.REDIRECT, ...payload }))
    },
    onFormSubmit: (formID: string, formName: string, payload: FormSubmitPayload, processId?: string) => {
      const evt: FormSubmitEvent = createEvent({
        event: EventType.FORM_SUBMIT,
        data: payload,
        formName,
        formID,
        ...(processId && { processId })
      })
      push(evt)
    }
  }

  return tracker
})()
